import { useLazyQuery, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { useParams } from 'react-router-dom';

import {
  Heading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const getAppointmentSegmentIdsQuery = graphql(`
  query getAppointmentSegmentIds($tenantId: uuid!, $appointmentId: uuid!) {
    appointmentSegments(
      where: {
        tenantId: { _eq: $tenantId }
        appointmentId: { _eq: $appointmentId }
      }
    ) {
      __typename
      id
    }
  }
`);

const getTranscriptsQuery = graphql(`
  query getTranscripts($segmentId: uuid!) {
    transcripts(
      where: { segmentId: { _eq: $segmentId } }
      orderBy: { createdAt: ASC }
    ) {
      __typename
      transcript
      transcriptType
      confidence
    }
  }
`);

interface LiveTranscript {
  string: string;
  confidence: number;
}

interface TranscriptWithConfidenceScoresProps {
  transcripts: LiveTranscript[];
}

const TranscriptWithConfidenceScores: React.FC<
  TranscriptWithConfidenceScoresProps
> = ({ transcripts }) => {
  return (
    <div>
      {transcripts.map((transcript, index) => (
        <div key={index} className="mb-1">
          <div>{transcript.string}</div>
          <div className="text-xs text-text-disabled">
            Confidence: {transcript.confidence}
          </div>
        </div>
      ))}
    </div>
  );
};

export const AdminTranscriptsPage: React.FC = () => {
  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };
  const toast = useToast();

  const [getTranscripts] = useLazyQuery(getTranscriptsQuery);

  const { data } = useQuery(getAppointmentSegmentIdsQuery, {
    variables: { appointmentId, tenantId },
  });

  const segmentIds = data?.appointmentSegments?.map((s) => s.id) ?? [];

  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null,
  );
  const [isFetchingTranscripts, setIsFetchingTranscripts] = useState(false);
  const [batchTranscript, setBatchTranscript] = useState<string | null>(null);
  const [liveTranscript, setLiveTranscript] = useState<string | null>(null);
  const [
    liveTranscriptWithConfidenceScores,
    setLiveTranscriptWithConfidenceScores,
  ] = useState<LiveTranscript[]>([]);

  // Strip formatting and reformat both types of transcripts in order
  // minimize extraneous differences in the diff viewer
  const processText = (text: string) => {
    // Remove extra spaces, new lines, and tabs
    const cleanedText = text.replace(/\s+/g, ' ').trim();

    // Split at punctuation points (. ? !)
    const splitText = cleanedText.split(/(?<=[.!?])\s*/);

    // Join the split text with new lines
    const formattedText = splitText.join('\n');

    return formattedText;
  };

  const handleSegmentSelect = async (segmentId: string) => {
    setSelectedSegmentId(segmentId);
    setIsFetchingTranscripts(true);

    const { data } = await getTranscripts({
      variables: { segmentId },
    });

    const transcripts = data?.transcripts ?? [];

    if (!transcripts.length) {
      toast.error('No transcripts found');
      setIsFetchingTranscripts(false);
      return;
    }

    const batch = transcripts.find((t) => t.transcriptType === 'BATCH');

    setBatchTranscript(processText(batch?.transcript ?? ''));

    const liveTranscripts = transcripts.filter(
      (t) => t.transcriptType === 'LIVE',
    );

    setLiveTranscriptWithConfidenceScores(
      liveTranscripts.map((t) => ({
        string: t.transcript ?? '',
        confidence: t.confidence ?? 0,
      })),
    );

    setLiveTranscript(
      processText(liveTranscripts.map((t) => t.transcript).join('\n')),
    );

    setIsFetchingTranscripts(false);
  };

  return (
    <div>
      <Stack fullWidth={false} gap={10}>
        {Boolean(segmentIds.length) && (
          <Stack className="max-w-[600px]" gap={2}>
            <Heading size="2xl" weight="semibold">
              Select a Segment ID
            </Heading>
            <Text size="xs" className="text-text-warning italic">
              Warning: This will show the transcript for the segment.
            </Text>
            <Select
              onValueChange={handleSegmentSelect}
              disabled={isFetchingTranscripts}
            >
              <SelectTrigger className="text-text-primary">
                <SelectValue className="text-text-primary">
                  {selectedSegmentId ?? 'Select a segment ID'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {segmentIds.map((segmentId) => (
                  <SelectItem
                    key={segmentId}
                    className="pl-7 focus:bg-bg-accent-quaternary"
                    value={segmentId}
                  >
                    {segmentId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Stack>
        )}

        {liveTranscript && batchTranscript && (
          <ReactDiffViewer
            leftTitle={'Live'}
            oldValue={liveTranscript ?? ''}
            rightTitle={'Batch'}
            newValue={batchTranscript ?? ''}
            splitView={true}
            showDiffOnly={true}
          />
        )}

        {Boolean(liveTranscriptWithConfidenceScores.length) && (
          <Stack fullWidth={false} gap={2}>
            <Heading size="3xl">Confidence Scores</Heading>
            <Text color="disabled" size="xs" className="italic">
              This metadata is for the Live Transcript.
            </Text>
            <TranscriptWithConfidenceScores
              transcripts={liveTranscriptWithConfidenceScores}
            />
          </Stack>
        )}
      </Stack>
    </div>
  );
};
